import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Check,
  ShieldCheck,
  Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { ASSETS, COMPANY, SERVICES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  phone: "",
  email: "",
  address: "",
  service_needed: "",
  preferred_contact: "phone",
  message: "",
};

const Quote = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    if (!form.address.trim()) e.address = "Please enter your address";
    if (!form.service_needed) e.service_needed = "Pick a service";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/quote-requests`, payload);
      setSubmitted(true);
      setForm(initialState);
      toast.success("Quote request sent! We’ll be in touch soon.");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your request. Please call us instead.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page" className="bg-white">
      <section
        className="relative pt-32 pb-16 md:pt-40 md:pb-20"
        data-testid="quote-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.photos.backyard})` }}
        />
        <div className="absolute inset-0 bg-[#0E3A1F]/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <span className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
            Free Quote
          </span>
          <h1
            data-testid="quote-headline"
            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl"
          >
            Request Your Free Lawn Care Quote
          </h1>
          <p
            data-testid="quote-subtext"
            className="mt-5 text-white/90 text-lg max-w-2xl"
          >
            Fill out the form below and Southern Greens Lawn Care will contact
            you as soon as possible.
          </p>
          <a
            href={COMPANY.telHref}
            data-testid="quote-page-call-link"
            className="mt-8 inline-flex items-center gap-3 bg-white text-[#0E3A1F] rounded-full px-6 py-4 font-bold text-lg md:text-2xl shadow-xl hover:-translate-y-0.5 transition-all"
          >
            <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#5BB23B] text-white">
              <Phone className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-[10px] tracking-widest uppercase text-[#4B5563]">
                Call Now
              </span>
              <span className="block text-2xl md:text-3xl font-extrabold">
                {COMPANY.phoneDisplay}
              </span>
            </span>
          </a>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <aside
            className="lg:col-span-2 bg-[#0E3A1F] text-white rounded-3xl p-8 md:p-10 self-start"
            data-testid="quote-info-card"
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold">
              Why homeowners call us first.
            </h2>
            <p className="mt-3 text-white/80">
              Honest pricing, reliable schedules, and crews that actually care
              about your yard.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Free, no-pressure quotes",
                "Locally owned & operated",
                "Residential & commercial",
                "On-time, every time",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-white/90">
                  <Check className="h-5 w-5 text-[#5BB23B] mt-0.5" />
                  <span className="font-medium">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 pt-8 border-t border-white/10 space-y-4 text-white/85">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 mt-0.5 text-[#5BB23B] shrink-0" />
                <a
                  href={COMPANY.telHref}
                  data-testid="quote-info-call"
                  className="font-bold text-lg hover:text-white"
                >
                  {COMPANY.phoneDisplay}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-0.5 text-[#5BB23B] shrink-0" />
                <span>{COMPANY.serviceArea}</span>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 mt-0.5 text-[#5BB23B] shrink-0" />
                <span>{COMPANY.hours}</span>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 mt-0.5 text-[#5BB23B] shrink-0" />
                <span>Your information stays private. No spam, ever.</span>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3" data-testid="quote-form-card">
            {submitted ? (
              <div
                data-testid="quote-success"
                className="bg-[#F5F5F0] border border-[#D4D4CE] rounded-3xl p-10 text-center"
              >
                <div className="mx-auto h-16 w-16 rounded-full bg-[#5BB23B]/15 flex items-center justify-center">
                  <Check className="h-8 w-8 text-[#5BB23B]" />
                </div>
                <h3 className="mt-5 font-display text-2xl md:text-3xl font-bold text-[#0A0A0A]">
                  Thanks — we’ve got it!
                </h3>
                <p className="mt-3 text-[#4B5563]">
                  Southern Greens Lawn Care will reach out as soon as possible
                  to talk about your yard. Need us sooner?{" "}
                  <a
                    href={COMPANY.telHref}
                    className="text-[#0E3A1F] font-bold underline"
                  >
                    Call {COMPANY.phoneDisplay}
                  </a>
                  .
                </p>
                <button
                  type="button"
                  data-testid="quote-submit-another"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 sg-btn-primary rounded-full px-6 py-3 font-semibold"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="quote-form"
                className="bg-white border border-[#D4D4CE] rounded-3xl p-7 md:p-10 shadow-sm"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    label="Full name"
                    required
                    error={errors.name}
                  >
                    <Input
                      data-testid="quote-input-name"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="Jane Doe"
                      className="h-12 bg-[#F5F5F0] border-transparent focus-visible:ring-2 focus-visible:ring-[#5BB23B]/40 focus-visible:border-[#5BB23B]"
                    />
                  </Field>
                  <Field
                    label="Phone number"
                    required
                    error={errors.phone}
                  >
                    <Input
                      data-testid="quote-input-phone"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="(479) 555-1234"
                      inputMode="tel"
                      className="h-12 bg-[#F5F5F0] border-transparent focus-visible:ring-2 focus-visible:ring-[#5BB23B]/40 focus-visible:border-[#5BB23B]"
                    />
                  </Field>
                  <Field label="Email (optional)" error={errors.email}>
                    <Input
                      data-testid="quote-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="you@email.com"
                      className="h-12 bg-[#F5F5F0] border-transparent focus-visible:ring-2 focus-visible:ring-[#5BB23B]/40 focus-visible:border-[#5BB23B]"
                    />
                  </Field>
                  <Field
                    label="Service address"
                    required
                    error={errors.address}
                  >
                    <Input
                      data-testid="quote-input-address"
                      value={form.address}
                      onChange={(e) => setField("address", e.target.value)}
                      placeholder="Street, City"
                      className="h-12 bg-[#F5F5F0] border-transparent focus-visible:ring-2 focus-visible:ring-[#5BB23B]/40 focus-visible:border-[#5BB23B]"
                    />
                  </Field>
                  <Field
                    label="Service needed"
                    required
                    error={errors.service_needed}
                    className="md:col-span-2"
                  >
                    <Select
                      value={form.service_needed}
                      onValueChange={(v) => setField("service_needed", v)}
                    >
                      <SelectTrigger
                        data-testid="quote-input-service"
                        className="h-12 bg-[#F5F5F0] border-transparent focus:ring-2 focus:ring-[#5BB23B]/40"
                      >
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem
                            key={s.slug}
                            value={s.title}
                            data-testid={`quote-service-option-${s.slug}`}
                          >
                            {s.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="Not sure — please advise">
                          Not sure — please advise
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field
                    label="Preferred contact method"
                    required
                    className="md:col-span-2"
                  >
                    <RadioGroup
                      data-testid="quote-input-contact"
                      value={form.preferred_contact}
                      onValueChange={(v) => setField("preferred_contact", v)}
                      className="flex flex-wrap gap-3"
                    >
                      {[
                        { v: "phone", label: "Phone Call" },
                        { v: "text", label: "Text Message" },
                        { v: "email", label: "Email" },
                      ].map((opt) => (
                        <label
                          key={opt.v}
                          htmlFor={`contact-${opt.v}`}
                          data-testid={`quote-contact-${opt.v}`}
                          className={`cursor-pointer flex items-center gap-2 px-4 py-2.5 rounded-full border transition-colors ${
                            form.preferred_contact === opt.v
                              ? "border-[#5BB23B] bg-[#5BB23B]/10 text-[#0E3A1F]"
                              : "border-[#D4D4CE] bg-white text-[#0A0A0A] hover:border-[#5BB23B]/60"
                          }`}
                        >
                          <RadioGroupItem
                            id={`contact-${opt.v}`}
                            value={opt.v}
                            className="sr-only"
                          />
                          <span className="text-sm font-medium">{opt.label}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </Field>
                  <Field label="Message / additional details" className="md:col-span-2">
                    <Textarea
                      data-testid="quote-input-message"
                      value={form.message}
                      onChange={(e) => setField("message", e.target.value)}
                      rows={5}
                      placeholder="Tell us about your yard, the frequency you’d like, gate codes, pets, etc."
                      className="bg-[#F5F5F0] border-transparent focus-visible:ring-2 focus-visible:ring-[#5BB23B]/40 focus-visible:border-[#5BB23B]"
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit"
                  className="mt-7 w-full sg-btn-dark rounded-2xl py-4 font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    "Sending…"
                  ) : (
                    <>
                      <Send className="h-5 w-5" /> Send My Request
                    </>
                  )}
                </button>
                <p className="mt-3 text-xs text-[#4B5563] text-center">
                  We typically respond within a few hours during business hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, error, children, className = "" }) => (
  <div className={className}>
    <Label className="text-sm font-semibold text-[#0A0A0A]">
      {label}
      {required && <span className="text-[#5BB23B]"> *</span>}
    </Label>
    <div className="mt-2">{children}</div>
    {error && (
      <p className="mt-1.5 text-xs text-red-600 font-medium">{error}</p>
    )}
  </div>
);

export default Quote;
